import { GoogleButton } from "./AuthCallback";
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Loader2, PlayCircle } from "lucide-react";

export default function Login() {
  const { login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/");
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel - form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md fade-in">
          <img src="/img/farmanaliza-banner.jpg?v=2" alt="Farmanaliza" className="lg:hidden w-full rounded-xl mb-8 aspect-[3/2] object-cover" data-testid="login-banner-img-mobile" />
          <div className="flex items-center gap-2 mb-12">
            <img src="/img/farmanaliza-logo.png?v=3" alt="Farmanaliza · Stocks, ventas, equipo" className="h-14 w-auto" data-testid="login-logo" />
          </div>

          <h1 className="font-display text-3xl font-extrabold tracking-tight mb-2">Bienvenido</h1>
          <p className="text-slate-600 mb-8">Accede para subir tus estudios y analizar tu stock.</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Email o usuario</label>
              <input
                data-testid="login-email-input"
                type="text"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input mt-1"
                placeholder="farmacia@tudominio.com o admin"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Contraseña</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input mt-1"
                placeholder="••••••••"
              />
              <div className="text-right mt-1">
                <Link to="/recuperar" className="text-xs text-slate-500 hover:text-slate-900 hover:underline" data-testid="login-forgot-link">¿Has olvidado la contraseña?</Link>
              </div>
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded border border-red-200">
                {error}
              </div>
            )}
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full justify-center"
            >
              {loading ? <><Loader2 size={16} className="animate-spin" /> Accediendo…</> : "Entrar"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5 text-[11px] uppercase tracking-widest text-slate-400">
            <span className="flex-1 border-t border-slate-200" /> o <span className="flex-1 border-t border-slate-200" />
          </div>
          <GoogleButton />

          <div className="mt-6 text-sm text-slate-600">
            ¿Aún no tienes cuenta?{" "}
            <Link data-testid="goto-register-link" to="/register" className="font-semibold text-slate-900 hover:underline">
              Crear cuenta
            </Link>
          </div>
          <Link to="/informacion" data-testid="goto-informacion-link"
                className="group mt-6 block relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#12305e] to-[#1f6fd1] p-5 text-white shadow-xl ring-1 ring-blue-900/20 hover:shadow-2xl hover:-translate-y-0.5 transition-[transform,box-shadow]">
            <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10 group-hover:scale-125 transition-transform duration-500" />
            <div className="absolute right-10 -bottom-10 h-24 w-24 rounded-full bg-emerald-400/20" />
            <div className="relative flex items-center gap-4">
              <span className="h-14 w-14 shrink-0 rounded-full bg-white text-[#1f6fd1] flex items-center justify-center shadow-lg animate-pulse group-hover:animate-none">
                <PlayCircle size={30} />
              </span>
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-300">Nuevo · vídeo</div>
                <div className="font-display text-lg sm:text-xl font-extrabold leading-tight">¿Quieres saber qué hace Farmanaliza?</div>
                <div className="text-sm text-blue-100 mt-0.5">Recupera el dinero de tu stock parado. Míralo en acción →</div>
              </div>
            </div>
          </Link>
        </div>
      </div>
      {/* Right panel - branded */}
      <div className="hidden lg:flex flex-1 flex-col bg-[#12305e] text-white overflow-hidden" data-testid="login-brand-panel">
        <img src="/img/farmanaliza-banner.jpg?v=2" alt="Farmanaliza · Gestión de stocks en farmacia" className="w-full aspect-[3/2] object-cover" data-testid="login-banner-img" />
        <div className="flex-1 flex flex-col justify-center gap-8 p-12">
          <p className="text-blue-100 text-lg max-w-xl">
            Clasificación ABCD, stock parado, ventas, equipo y proveedores. Todo en una sola plataforma.
          </p>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 text-sm">
            {[["Clasifica", "7 niveles ABCD"], ["Detecta", "Stock parado"], ["Compara", "Mes a mes"], ["Exporta", "Excel y PDF"]].map(([k, v]) => (
              <div key={k} className="border-l-2 border-[#5aa2f0] pl-3">
                <div className="text-xs text-blue-300 uppercase tracking-widest">{k}</div>
                <div className="font-display text-xl font-bold mt-1">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
